import { useEffect, useRef, useState } from "react";
import { DemographicQuestion, QuestionType } from "../types/event";

interface DemographicQuestionsBuilderProps {
  defaultValue?: DemographicQuestion[] | null;
}

function generateId(): string {
  return crypto.randomUUID();
}

function createEmptyQuestion(): DemographicQuestion {
  return {
    id: generateId(),
    type: "text",
    question: "",
    required: true,
    options: [],
  };
}

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  text: "Text (Fill in the blank)",
  dropdown: "Dropdown (Single select)",
  radio: "Radio Buttons (Single select)",
  checkbox: "Checkboxes (Multi-select)",
};

const TYPES_WITH_OPTIONS: QuestionType[] = ["dropdown", "radio", "checkbox"];

function getQuestionErrors(q: DemographicQuestion): { question?: string; options?: string } {
  const errors: { question?: string; options?: string } = {};
  if (!q.question.trim()) {
    errors.question = "Question text is required.";
  }
  if (TYPES_WITH_OPTIONS.includes(q.type) && q.options.length === 0) {
    errors.options = "At least one option is required.";
  }
  return errors;
}

function hasAnyErrors(questions: DemographicQuestion[]): string {
  for (let i = 0; i < questions.length; i++) {
    const errs = getQuestionErrors(questions[i]);
    if (errs.question || errs.options) {
      return `Q${i + 1}: ${errs.question || errs.options}`;
    }
  }
  return "";
}

export default function DemographicQuestionsBuilder({
  defaultValue,
}: DemographicQuestionsBuilderProps) {
  const hasDefault = Array.isArray(defaultValue) && defaultValue.length > 0;
  const [questions, setQuestions] = useState<DemographicQuestion[]>(
    hasDefault ? defaultValue : []
  );
  const [showErrors, setShowErrors] = useState(false);
  const hiddenRef = useRef<HTMLInputElement>(null);

  const serializedValue = questions.length > 0
    ? JSON.stringify(questions)
    : "";

  // Keep the hidden input's custom validity in sync with the questions state
  useEffect(() => {
    const msg = hasAnyErrors(questions);
    hiddenRef.current?.setCustomValidity(msg);
  }, [questions]);

  const updateQuestion = (id: string, patch: Partial<DemographicQuestion>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...patch } : q))
    );
  };

  const removeQuestion = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    setQuestions((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addOption = (questionId: string, option: string) => {
    if (!option.trim()) return;
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: [...q.options, option.trim()] }
          : q
      )
    );
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.filter((_, i) => i !== optionIndex) }
          : q
      )
    );
  };

  return (
    <div className="col-span-6">
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Demographic Questions</h3>

        <div className="space-y-4">
          {questions.map((q, index) => (
            <QuestionCard
              key={q.id}
              question={q}
              index={index}
              total={questions.length}
              showErrors={showErrors}
              onUpdate={(patch) => updateQuestion(q.id, patch)}
              onRemove={() => removeQuestion(q.id)}
              onMoveUp={() => moveQuestion(index, -1)}
              onMoveDown={() => moveQuestion(index, 1)}
              onAddOption={(opt) => addOption(q.id, opt)}
              onRemoveOption={(optIdx) => removeOption(q.id, optIdx)}
            />
          ))}

          <button
            type="button"
            onClick={() =>
              setQuestions((prev) => [...prev, createEmptyQuestion()])
            }
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            + Add Question
          </button>
        </div>
      </div>

      <input
        ref={hiddenRef}
        type="hidden"
        name="signup_questions"
        value={serializedValue}
        // When native validation fires on this hidden input, reveal inline errors
        onInvalid={() => setShowErrors(true)}
      />
    </div>
  );
}

interface QuestionCardProps {
  question: DemographicQuestion;
  index: number;
  total: number;
  showErrors: boolean;
  onUpdate: (patch: Partial<DemographicQuestion>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddOption: (option: string) => void;
  onRemoveOption: (optionIndex: number) => void;
}

function QuestionCard({
  question,
  index,
  total,
  showErrors,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onAddOption,
  onRemoveOption,
}: QuestionCardProps) {
  const [newOption, setNewOption] = useState("");
  const needsOptions = TYPES_WITH_OPTIONS.includes(question.type);
  const errors = showErrors ? getQuestionErrors(question) : {};

  const handleAddOption = () => {
    if (newOption.trim()) {
      onAddOption(newOption);
      setNewOption("");
    }
  };

  return (
    <div className={`bg-gray-50 border rounded-lg p-4 ${errors.question || errors.options ? "border-red-300" : "border-gray-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-600">
          Question-{index + 1}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-600 ml-1"
            title="Delete question"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-3">
        <div className="col-span-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Type
          </label>
          <select
            value={question.type}
            onChange={(e) => {
              const newType = e.target.value as QuestionType;
              onUpdate({
                type: newType,
                options: TYPES_WITH_OPTIONS.includes(newType) ? question.options : [],
              });
            }}
            className="block w-full rounded-md border-gray-300 sm:text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {(Object.keys(QUESTION_TYPE_LABELS) as QuestionType[]).map((t) => (
              <option key={t} value={t}>
                {QUESTION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Text
          </label>
          <input
            type="text"
            value={question.question}
            onChange={(e) => onUpdate({ question: e.target.value })}
            placeholder="Enter your question..."
            className={`block w-full rounded-md sm:text-sm ${
              errors.question
                ? "border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            }`}
          />
          {errors.question && (
            <p className="mt-1 text-sm text-red-600">{errors.question}</p>
          )}
        </div>

        {needsOptions && (
          <div className="col-span-6">
            <label className={`block text-sm font-medium mb-1 ${errors.options ? "text-red-600" : "text-gray-700"}`}>
              Options
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {question.options.map((opt, optIdx) => (
                <span
                  key={optIdx}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700"
                >
                  {opt}
                  <button
                    type="button"
                    onClick={() => onRemoveOption(optIdx)}
                    className="text-primary-500 hover:text-primary-700"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
                placeholder="Add an option (press 'enter' to add)"
                className={`block w-full rounded-md sm:text-sm ${
                  errors.options
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                }`}
              />
              <button
                type="button"
                onClick={handleAddOption}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Add
              </button>
            </div>
            {errors.options && (
              <p className="mt-1 text-sm text-red-600">{errors.options}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
